import { announce } from "../lib/announce.ts";
import { getButtonLabel, setButtonLabel, setButtonPriceVisible } from "../lib/button-label.ts";
import { cartAdd, cartChange, fetchCartSection } from "../lib/cart-api.ts";
import { Component } from "../lib/component.ts";
import { decodeEntities } from "../lib/html-entities.ts";
import { scrollIntoContainer } from "../lib/scroll-into-container.ts";
import type {
  InputHandler,
  TabConfig,
  FontConfig,
  PreviewOptions,
  Preview as PreviewClass,
} from "../../product-personalization/index.ts";

type Core = typeof import("../../product-personalization/index.ts");

const PREVIEW_KEY_PREFIX = "personalizationPreview:";

interface FontPickerLike extends HTMLElement {
  value: string;
  setFonts(fonts: FontConfig[], defaultFont: string): void;
  getDisplayName(value: string): string;
  getCartLabel(value: string): string;
}

interface PersonalizationRefs {
  stage?: HTMLElement;

  config?: HTMLScriptElement;

  productsConfig?: HTMLScriptElement;

  tabButton?: HTMLButtonElement[];

  tabPanel?: HTMLElement[];

  font?: FontPickerLike | FontPickerLike[];

  addToCart?: HTMLButtonElement;

  previewStatus?: HTMLElement;

  stepButton?: HTMLButtonElement[];

  stepPreview?: HTMLElement[];

  productPanel?: HTMLElement[];

  summaryPanel?: HTMLElement;

  stagePlaceholder?: HTMLElement;

  backStep?: HTMLButtonElement;

  disclaimer?: HTMLElement;
}

interface EditContext {
  key: string;
  quantity: number;
  properties: Record<string, string>;
  feeLineKey?: string;
}

interface ProductDescriptor {
  variantId: number;
  productTitle: string;
  sceneUrl: string;
  photoUrl: string;
  // Validated downstream by the personalization bundle's Zod schema. Kept
  // as unknown here so the theme bundle does not duplicate validation.
  config: unknown;
}

interface ProductState {
  handlers: (InputHandler | null)[];
  activeTabIndex: number;
  savedRestoreData: (Record<string, unknown> | null)[];
  tabButtons: HTMLButtonElement[];
  tabPanels: HTMLElement[];
  fontEl: FontPickerLike;
}

interface ModalPanelLike extends HTMLElement {
  close(): void;
}

interface CartDrawerLike extends HTMLElement {
  openSuppressingAnalytics(): void;
}

function clampTabIndex(value: unknown, max: number): number {
  if (typeof value !== "number") return 0;
  return Math.max(0, Math.min(value, max));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parseProductDescriptor(value: unknown): ProductDescriptor | null {
  if (!isRecord(value)) return null;
  const { variantId, productTitle, sceneUrl, photoUrl, config } = value;
  if (typeof variantId !== "number") return null;
  if (typeof productTitle !== "string") return null;
  if (typeof sceneUrl !== "string") return null;
  if (typeof photoUrl !== "string") return null;
  if (!isRecord(config)) return null;
  return {
    variantId,
    productTitle,
    sceneUrl: normalizeAssetUrl(sceneUrl),
    photoUrl: normalizeAssetUrl(photoUrl),
    config,
  };
}

function normalizeAssetUrl(url: string): string {
  if (!url) return "";
  try {
    const parsed = new URL(url, location.origin);
    if (parsed.origin === location.origin) {
      return parsed.pathname + parsed.search;
    }
    // Protocol-relative URLs resolve to HTTP on dev servers, causing
    // CORS failures. Force HTTPS for cross-origin CDN URLs.
    parsed.protocol = "https:";
    return parsed.href;
  } catch {
    return url;
  }
}

function isModalPanelLike(el: Element | null): el is ModalPanelLike {
  if (!(el instanceof HTMLElement)) return false;
  const close: unknown = Reflect.get(el, "close");
  return typeof close === "function";
}

function isCartDrawerLike(el: Element | null): el is CartDrawerLike {
  if (!(el instanceof HTMLElement)) return false;
  const fn: unknown = Reflect.get(el, "openSuppressingAnalytics");
  return typeof fn === "function";
}

function isFontPickerLike(el: Element | undefined | null): el is FontPickerLike {
  if (!(el instanceof HTMLElement)) return false;
  const setFonts: unknown = Reflect.get(el, "setFonts");
  const value: unknown = Reflect.get(el, "value");
  const getDisplayName: unknown = Reflect.get(el, "getDisplayName");
  const getCartLabel: unknown = Reflect.get(el, "getCartLabel");
  return (
    typeof setFonts === "function" &&
    typeof value === "string" &&
    typeof getDisplayName === "function" &&
    typeof getCartLabel === "function"
  );
}

class ProductPersonalization extends Component<PersonalizationRefs> {
  #preview: PreviewClass | null = null;

  #stalePreviews: PreviewClass[] = [];

  #handlers: (InputHandler | null)[] = [];

  #fontEl: FontPickerLike | null = null;

  #activeTabIndex = 0;

  #editContext: EditContext | null = null;

  #isBundleMode = false;

  #products: ProductDescriptor[] = [];

  #productStates: ProductState[] = [];

  #currentProductIndex = 0;

  #core: Core | null = null;

  #bundlePreviewImages = new Map<number, string>();

  connectedCallback(): void {
    super.connectedCallback();
    void this.#init();
  }

  disconnectedCallback(): void {
    this.#preview?.dispose();
    this.#preview = null;
    this.#disposeStalePreviews();
    this.#bundlePreviewImages.clear();
    super.disconnectedCallback();
  }

  #disposeStalePreviews(): void {
    for (const preview of this.#stalePreviews) preview.dispose();
    this.#stalePreviews = [];
  }

  setEditContext(context: EditContext): void {
    this.#editContext = context;
    this.#applyEditButtonText();

    if (this.#isBundleMode && this.#productStates.length > 0) {
      this.#restoreBundleFromContext();
    } else if (this.#handlers.length > 0) {
      this.#restoreFromContext();
    }
  }

  #applyEditButtonText(): void {
    const button = this.refs.addToCart;
    if (!button) return;

    const editLoading = button.dataset.editLoadingText;
    if (editLoading) button.dataset.loadingText = editLoading;

    const editConfirm = button.dataset.editConfirmText;
    if (!editConfirm) return;

    // Editing from the cart: the line is already priced, so no price on "Confirm".
    setButtonPriceVisible(button, false);
    if (this.dataset.bundleMode === "true") {
      button.dataset.confirmText = editConfirm;
    } else {
      setButtonLabel(button, editConfirm);
    }
  }

  #restoreFromContext(): void {
    const hidden = this.#editContext?.properties._personalization;
    if (!hidden) return;
    try {
      const data: unknown = JSON.parse(decodeEntities(hidden));
      if (!isRecord(data)) return;
      const tabIndex = clampTabIndex(data.tabIndex, this.#handlers.length - 1);

      this.#setActiveTabIndex(tabIndex);

      const handler = this.#handlers[tabIndex];
      if (!handler) return;

      handler.restore(data);
      this.#triggerBoundedFieldCounters(this.#getActivePanel());

      if (this.#preview) {
        this.#preview.setInputHandler(handler);
      }

      this.#updatePreviewStatus();
    } catch {
      // ignore malformed data
    }
  }

  async #init(): Promise<void> {
    const coreUrl = this.dataset.coreUrl;
    if (!coreUrl) return;

    // webpackIgnore / @vite-ignore keep this a native import(): the core is a
    // separate asset resolved by URL at runtime, not a chunk of this bundle.
    const core: Core = await import(/* webpackIgnore: true */ /* @vite-ignore */ coreUrl);

    const productsConfigEl = this.refs.productsConfig;
    if (productsConfigEl) {
      this.#isBundleMode = true;
      this.#core = core;
      this.#initBundleMode(core);
    } else {
      this.#initSingleMode(core);
    }
  }

  #initSingleMode(core: Core): void {
    const configEl = this.refs.config;
    if (!configEl) return;

    const config: unknown = JSON.parse(configEl.textContent ?? "{}");
    if (typeof config !== "object" || config === null) return;
    const tabs = this.#extractTabs(config);
    const fontRef = this.refs.font;
    const fontEl = Array.isArray(fontRef) ? fontRef[0] : fontRef;
    if (!isFontPickerLike(fontEl)) return;
    this.#fontEl = fontEl;

    const tabPanels = this.#asElementArray(this.refs.tabPanel);

    this.#handlers = tabs.map((tab, i) => {
      const panel = tabPanels[i];
      if (!(panel instanceof HTMLElement)) return null;
      return this.#createHandler(core, tab.inputType, panel, fontEl);
    });

    const firstHandler = this.#handlers[0];
    if (!firstHandler) return;

    const stage = this.refs.stage;
    if (!stage) return;

    const previewOptions: PreviewOptions = {
      stage,
      config,
      sceneUrl: this.dataset.sceneUrl ?? "",
      photoUrl: this.dataset.photoUrl ?? "",
      inputHandler: firstHandler,
      onError: () => this.#showStageError(),
    };
    this.#preview = new core.Preview(previewOptions);

    const tabButtons = this.#asButtonArray(this.refs.tabButton);
    this.#wireTabNavigation(
      tabButtons,
      () => this.#activeTabIndex,
      (idx) => this.#switchTab(idx),
    );

    const addToCart = this.refs.addToCart;
    addToCart?.addEventListener("click", this.#handleConfirm, { signal: this.signal });

    this.#wireInputListeners(this);

    if (this.#editContext) {
      this.#restoreFromContext();
    }
  }

  #initBundleMode(core: Core): void {
    const productsConfigEl = this.refs.productsConfig;
    if (!productsConfigEl) return;
    const rawList: unknown = JSON.parse(productsConfigEl.textContent ?? "[]");
    if (!Array.isArray(rawList)) return;

    this.#products = rawList
      .map(parseProductDescriptor)
      .filter((p): p is ProductDescriptor => p !== null);

    const productPanels = this.#asElementArray(this.refs.productPanel);
    const fontRef = this.refs.font;
    const fontEls: FontPickerLike[] = Array.isArray(fontRef) ? fontRef : fontRef ? [fontRef] : [];

    for (let i = 0; i < this.#products.length; i++) {
      const product = this.#products[i];
      const panel = productPanels[i];
      const fontEl = fontEls[i];
      if (!(panel instanceof HTMLElement) || !isFontPickerLike(fontEl)) continue;

      const tabs = this.#extractTabs(product.config);

      const panelTabButtons = [...panel.querySelectorAll("[data-tab-index]")].filter(
        (el): el is HTMLButtonElement => el instanceof HTMLButtonElement,
      );
      const panelTabPanels = [...panel.querySelectorAll('[ref="tabPanel[]"]')].filter(
        (el): el is HTMLElement => el instanceof HTMLElement,
      );

      const handlers = tabs.map((tab, j) => {
        const tabPanel = panelTabPanels[j];
        if (!tabPanel) return null;
        return this.#createHandler(core, tab.inputType, tabPanel, fontEl);
      });

      this.#productStates.push({
        handlers,
        activeTabIndex: 0,
        savedRestoreData: handlers.map(() => null),
        tabButtons: panelTabButtons,
        tabPanels: panelTabPanels,
        fontEl,
      });
    }

    const stepButtons = this.#asButtonArray(this.refs.stepButton);
    for (const btn of stepButtons) {
      btn.addEventListener(
        "click",
        () => {
          const idx = btn.dataset.stepIndex;
          this.#switchProductStep(idx === "summary" ? this.#products.length : Number(idx));
        },
        { signal: this.signal },
      );
    }

    const addToCart = this.refs.addToCart;
    addToCart?.addEventListener("click", this.#handleConfirm, { signal: this.signal });

    const backStep = this.refs.backStep;
    backStep?.addEventListener(
      "click",
      () => this.#switchProductStep(this.#currentProductIndex - 1, { scrollToTop: true }),
      { signal: this.signal },
    );

    for (let i = 0; i < this.#productStates.length; i++) {
      this.#wireProductInputListeners(i);
      const state = this.#productStates[i];
      if (state) {
        this.#wireTabNavigation(
          state.tabButtons,
          () => state.activeTabIndex,
          (idx) => this.#switchBundleTab(i, idx),
        );
      }
    }

    if (this.#products.length === 1 && addToCart) {
      setButtonLabel(addToCart, addToCart.dataset.summaryText ?? "");
      setButtonPriceVisible(addToCart, false);
    }

    if (this.#editContext) {
      this.#restoreBundleFromContext();
    } else {
      this.#loadProductPreview(0);
    }
  }

  #extractTabs(config: unknown): TabConfig[] {
    if (!isRecord(config)) return [];
    const tabs = config.tabs;
    if (!Array.isArray(tabs)) return [];
    return tabs.filter((t): t is TabConfig => isRecord(t));
  }

  #loadProductPreview(index: number): void {
    const product = this.#products[index];
    const state = this.#productStates[index];
    if (!product || !state) return;

    const stage = this.refs.stage;
    if (!stage) return;

    const handler = state.handlers[state.activeTabIndex];
    if (!handler || !this.#core) {
      this.#preview?.dispose();
      this.#preview = null;
      this.#disposeStalePreviews();
      return;
    }

    if (this.#preview) this.#stalePreviews.push(this.#preview);
    this.#preview = null;

    const cachedPreview = this.#bundlePreviewImages.get(index);
    const bridgeImage = cachedPreview ?? product.photoUrl;
    stage.style.backgroundImage = bridgeImage ? `url('${bridgeImage}')` : "";

    const previewOptions: PreviewOptions = {
      stage,
      config: product.config,
      sceneUrl: product.sceneUrl,
      photoUrl: product.photoUrl,
      inputHandler: handler,
      onError: () => this.#showStageError(),
      onReady: () => this.#disposeStalePreviews(),
    };
    this.#preview = new this.#core.Preview(previewOptions);
  }

  #switchProductStep(index: number, options?: { scrollToTop?: boolean }): void {
    if (index === this.#currentProductIndex && index < this.#products.length) return;

    this.#saveCurrentProductState();

    if (this.#preview && this.#currentProductIndex < this.#products.length) {
      const dataUrl = this.#preview.captureImage();
      if (dataUrl) this.#bundlePreviewImages.set(this.#currentProductIndex, dataUrl);
    }

    const productPanels = this.#asElementArray(this.refs.productPanel);
    const summaryPanel = this.refs.summaryPanel;
    const stage = this.refs.stage;
    const stagePlaceholder = this.refs.stagePlaceholder;

    const stepButtons = this.#asButtonArray(this.refs.stepButton);
    for (const btn of stepButtons) {
      const btnIdx = btn.dataset.stepIndex;
      const isCurrent =
        btnIdx === "summary" ? index === this.#products.length : Number(btnIdx) === index;

      if (isCurrent) {
        btn.setAttribute("aria-current", "step");
      } else {
        btn.removeAttribute("aria-current");
      }

      if (btnIdx === "summary") {
        if (index >= this.#products.length) btn.disabled = false;
      } else if (Number(btnIdx) <= index) {
        btn.disabled = false;
      }
    }

    const button = this.refs.addToCart;

    if (index === this.#products.length) {
      this.#currentProductIndex = index;
      for (const panel of productPanels) {
        if (panel instanceof HTMLElement) panel.hidden = true;
      }
      if (stage) stage.hidden = true;
      if (stagePlaceholder) stagePlaceholder.hidden = false;
      this.#preview?.dispose();
      this.#preview = null;
      this.#disposeStalePreviews();

      if (summaryPanel) {
        summaryPanel.hidden = false;
        this.#renderSummary();
      }

      if (button) {
        setButtonLabel(button, button.dataset.confirmText ?? "");
        // The price belongs only to the final "Add to Cart" step (never when editing).
        setButtonPriceVisible(button, !this.#editContext);
      }
    } else {
      if (summaryPanel) summaryPanel.hidden = true;
      if (stage) stage.hidden = false;
      if (stagePlaceholder) stagePlaceholder.hidden = true;

      for (let i = 0; i < productPanels.length; i++) {
        const panel = productPanels[i];
        if (panel instanceof HTMLElement) panel.hidden = i !== index;
      }

      this.#currentProductIndex = index;
      this.#loadProductPreview(index);
      this.#restoreProductState(index);

      if (button) {
        const isLastProduct = index === this.#products.length - 1;
        setButtonLabel(
          button,
          (isLastProduct ? button.dataset.summaryText : button.dataset.nextText) ?? "",
        );
        setButtonPriceVisible(button, false);
      }
    }

    const backStepBtn = this.refs.backStep;
    if (backStepBtn) backStepBtn.hidden = index === 0;

    const disclaimer = this.refs.disclaimer;
    if (disclaimer) disclaimer.hidden = index !== this.#products.length;

    if (options?.scrollToTop) {
      this.closest('[role="dialog"]')?.scrollTo({ top: 0, behavior: "instant" });
    }

    this.#updateAllStepPreviews();

    const activeStepBtn = stepButtons.find((btn) => btn.getAttribute("aria-current") === "step");
    const stepWrapper = activeStepBtn?.parentElement;
    const stepsContainer = stepWrapper?.parentElement;
    if (stepsContainer && stepWrapper) {
      scrollIntoContainer(stepsContainer, stepWrapper, { behavior: "smooth" });
    }
  }

  #saveCurrentProductState(): void {
    const state = this.#productStates[this.#currentProductIndex];
    if (!state) return;

    for (let i = 0; i < state.handlers.length; i++) {
      const handler = state.handlers[i];
      if (handler) {
        state.savedRestoreData[i] = handler.getRestoreData();
      }
    }
  }

  #resetFormInputs(container: HTMLElement): void {
    for (const input of container.querySelectorAll("input, textarea")) {
      if (!(input instanceof HTMLInputElement) && !(input instanceof HTMLTextAreaElement)) continue;
      if (input instanceof HTMLInputElement && input.type === "checkbox") {
        input.checked = input.hasAttribute("checked");
      } else {
        input.value = "";
      }
      input.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }

  #resetFormState(): void {
    if (this.#isBundleMode) {
      for (const state of this.#productStates) {
        state.activeTabIndex = 0;
        state.savedRestoreData = state.handlers.map(() => null);
        for (const panel of state.tabPanels) {
          this.#resetFormInputs(panel);
          this.#clearPanelError(panel);
        }
      }
      this.#bundlePreviewImages.clear();
      this.#switchProductStep(0);

      const stepButtons = this.#asButtonArray(this.refs.stepButton);
      for (let i = 1; i < stepButtons.length; i++) {
        stepButtons[i].disabled = true;
      }
    } else {
      this.#activeTabIndex = 0;
      this.#setActiveTabIndex(0);
      const tabPanels = this.#asElementArray(this.refs.tabPanel);
      for (const panel of tabPanels) {
        if (!(panel instanceof HTMLElement)) continue;
        this.#resetFormInputs(panel);
        this.#clearPanelError(panel);
      }
      const handler = this.#handlers[0];
      if (handler && this.#preview) {
        this.#preview.setInputHandler(handler);
      }
    }
  }

  #restoreProductState(index: number): void {
    const state = this.#productStates[index];
    if (!state) return;

    this.#setBundleActiveTab(index, state.activeTabIndex);

    for (let i = 0; i < state.handlers.length; i++) {
      const handler = state.handlers[i];
      const saved = state.savedRestoreData[i];
      if (handler && saved) {
        handler.restore(saved);
        this.#triggerBoundedFieldCounters(state.tabPanels[i]);
      }
    }

    const activeHandler = state.handlers[state.activeTabIndex];
    if (activeHandler && this.#preview) {
      this.#preview.setInputHandler(activeHandler);
    }
  }

  #wireProductInputListeners(productIndex: number): void {
    const state = this.#productStates[productIndex];
    if (!state) return;

    const panel = this.#asElementArray(this.refs.productPanel)[productIndex];
    if (!(panel instanceof HTMLElement)) return;

    for (const field of panel.querySelectorAll('input:not([type="checkbox"]), textarea')) {
      field.addEventListener(
        "input",
        () => {
          this.#clearPanelError(state.tabPanels[state.activeTabIndex]);
          this.#updateBundlePreviews(productIndex);
        },
        { signal: this.signal },
      );
    }

    for (const field of panel.querySelectorAll('[name="dots"]')) {
      field.addEventListener(
        "input",
        () => {
          this.#updateBundlePreviews(productIndex);
        },
        { signal: this.signal },
      );
    }
  }

  #switchBundleTab(productIndex: number, tabIndex: number): void {
    const state = this.#productStates[productIndex];
    if (!state || tabIndex === state.activeTabIndex) return;

    this.#setBundleActiveTab(productIndex, tabIndex);
    this.#clearPanelError(state.tabPanels[tabIndex]);
    this.#updateBundlePreviews(productIndex);

    const handler = state.handlers[tabIndex];
    if (handler && this.#preview) {
      this.#preview.setInputHandler(handler);
    }
  }

  #setBundleActiveTab(productIndex: number, tabIndex: number): void {
    const state = this.#productStates[productIndex];
    if (!state) return;

    state.activeTabIndex = tabIndex;

    for (let i = 0; i < state.tabPanels.length; i++) {
      state.tabPanels[i].hidden = i !== tabIndex;
    }
    for (let i = 0; i < state.tabButtons.length; i++) {
      const isActive = i === tabIndex;
      state.tabButtons[i].setAttribute("aria-selected", String(isActive));
      state.tabButtons[i].tabIndex = isActive ? 0 : -1;
    }
  }

  #updateBundlePreviews(productIndex: number): void {
    const state = this.#productStates[productIndex];
    if (!state) return;
    const handler = state.handlers[state.activeTabIndex];
    if (handler) {
      this.#setPreviewStatus(handler, this.#products[productIndex]?.productTitle ?? "");
    }
    this.#updateStepPreview(productIndex);
  }

  #handleBundleConfirm(): void {
    if (this.#currentProductIndex < this.#products.length) {
      const state = this.#productStates[this.#currentProductIndex];
      if (state) {
        const handler = state.handlers[state.activeTabIndex];
        if (handler && handler.getText() && !handler.validate()) {
          this.#showValidationError(state.tabPanels[state.activeTabIndex], handler);
          return;
        }
      }
      this.#switchProductStep(this.#currentProductIndex + 1, { scrollToTop: true });
    } else {
      this.#saveCurrentProductState();

      const properties = this.#assembleBundleProperties();
      properties._line_id = this.#editContext?.properties._line_id ?? crypto.randomUUID();
      void this.#submitWithProperties(properties);
    }
  }

  #assembleBundleProperties(): Record<string, string> {
    const properties: Record<string, string> = {};
    const summaryParts: string[] = [];
    const restoreProducts: Record<string, unknown>[] = [];

    for (let i = 0; i < this.#products.length; i++) {
      const product = this.#products[i];
      const state = this.#productStates[i];
      if (!product || !state) continue;

      const handler = state.handlers[state.activeTabIndex];
      const text = handler?.getText() ?? "";
      const font = handler?.getFont() ?? "";

      restoreProducts.push({
        variantId: product.variantId,
        tabIndex: state.activeTabIndex,
        ...(handler?.getRestoreData() ?? {}),
      });

      const title = product.productTitle;
      // Key format parsed in cart-drawer.liquid for per-product display
      properties[`Personalization (${title})`] = text;
      properties[`Font (${title})`] = text ? state.fontEl.getCartLabel(font) : "";
      summaryParts.push(`${title}: ${text || "—"}`);
    }

    properties["Personalization"] = summaryParts.join("\n");
    properties["_personalization"] = JSON.stringify({ products: restoreProducts });

    return properties;
  }

  #renderSummary(): void {
    const summaryPanel = this.refs.summaryPanel;
    if (!summaryPanel) return;

    summaryPanel.innerHTML = "";

    const heading = document.createElement("p");
    heading.className = "personalization__summary-heading";
    heading.textContent = this.dataset.setReadyText ?? "";

    const items = document.createElement("div");
    items.className = "personalization__summary-items";

    for (let i = 0; i < this.#products.length; i++) {
      const product = this.#products[i];
      const state = this.#productStates[i];
      if (!product || !state) continue;

      const handler = state.handlers[state.activeTabIndex];
      const text = handler?.getText() ?? "";

      const item = document.createElement("div");
      item.className = "personalization__summary-item";

      const titleEl = document.createElement("span");
      titleEl.className = "personalization__summary-item-title mono";
      titleEl.textContent = product.productTitle;

      const textEl = document.createElement("span");
      textEl.className = "personalization__summary-item-text";
      textEl.textContent = text || "—";

      item.append(titleEl, textEl);
      items.append(item);
    }

    summaryPanel.append(heading, items);

    const stagePlaceholder = this.refs.stagePlaceholder;
    if (stagePlaceholder) {
      stagePlaceholder.innerHTML = "";
      const grid = document.createElement("div");
      grid.className = "personalization__preview-grid";

      const count = this.#products.length;
      const productImage = this.dataset.productImage;
      const showProductImage = count <= 3 && Boolean(productImage);
      const maxPreviews = count > 4 ? 4 : count;

      if (showProductImage && productImage) {
        const productImg = document.createElement("img");
        productImg.src = productImage;
        productImg.alt = "";
        productImg.className = "personalization__preview-grid-image";
        grid.append(productImg);
      }

      for (let i = 0; i < maxPreviews; i++) {
        const dataUrl = this.#bundlePreviewImages.get(i);
        if (!dataUrl) continue;
        const img = document.createElement("img");
        img.src = dataUrl;
        img.alt = this.#products[i]?.productTitle ?? "";
        img.className = "personalization__preview-grid-image";
        grid.append(img);
      }
      stagePlaceholder.append(grid);
    }
  }

  #updateStepPreview(productIndex: number): void {
    const preview = this.#asElementArray(this.refs.stepPreview)[productIndex];
    const state = this.#productStates[productIndex];
    if (!preview || !state) return;

    const handler = state.handlers[state.activeTabIndex];
    preview.textContent = handler?.getText() ?? "";
  }

  #updateAllStepPreviews(): void {
    for (let i = 0; i < this.#products.length; i++) {
      this.#updateStepPreview(i);
    }
  }

  #restoreBundleFromContext(): void {
    const hidden = this.#editContext?.properties._personalization;
    if (!hidden) return;

    try {
      const data: unknown = JSON.parse(decodeEntities(hidden));
      if (!isRecord(data)) return;
      const products = data.products;
      if (!Array.isArray(products)) return;

      for (let i = 0; i < products.length && i < this.#productStates.length; i++) {
        const entry: unknown = products[i];
        const state = this.#productStates[i];
        if (!isRecord(entry) || !state) continue;

        const tabIndex = clampTabIndex(entry.tabIndex, state.handlers.length - 1);

        state.activeTabIndex = tabIndex;
        state.savedRestoreData[tabIndex] = entry;
      }

      // Restore all products so getText() works for step previews.
      // Only product 0 needs full DOM setup; the rest just need handler data.
      this.#restoreProductState(0);
      for (let i = 1; i < this.#productStates.length; i++) {
        const state = this.#productStates[i];
        if (!state) continue;
        for (let j = 0; j < state.handlers.length; j++) {
          const handler = state.handlers[j];
          const saved = state.savedRestoreData[j];
          if (handler && saved) handler.restore(saved);
        }
      }
      this.#loadProductPreview(0);
      this.#updateAllStepPreviews();

      const stepButtons = this.#asButtonArray(this.refs.stepButton);
      for (const btn of stepButtons) {
        btn.disabled = false;
      }
    } catch {
      // ignore malformed data
    }
  }

  #asElementArray<T extends Element>(ref: T | T[] | undefined): T[] {
    if (Array.isArray(ref)) return ref;
    return ref ? [ref] : [];
  }

  #asButtonArray(ref: HTMLButtonElement | HTMLButtonElement[] | undefined): HTMLButtonElement[] {
    if (Array.isArray(ref)) return ref;
    return ref ? [ref] : [];
  }

  #createHandler(
    core: Core,
    inputType: string,
    panel: HTMLElement,
    fontEl: FontPickerLike,
  ): InputHandler | null {
    const {
      createInitialsHandler,
      createMessageHandler,
      createLetterheadHandler,
      createAddressHandler,
      createDateHandler,
    } = core;

    switch (inputType) {
      case "initials": {
        const initials = panel.querySelector('[name="initials"]');
        const dots = panel.querySelector('[name="dots"]');
        if (initials instanceof HTMLInputElement && dots instanceof HTMLInputElement) {
          return createInitialsHandler({ initials, dots, font: fontEl });
        }
        break;
      }
      case "date": {
        const date = panel.querySelector('[name="date"]');
        if (date instanceof HTMLInputElement) {
          return createDateHandler({ date, font: fontEl });
        }
        break;
      }
      case "message": {
        const message = panel.querySelector('[name="message"]');
        if (message instanceof HTMLInputElement || message instanceof HTMLTextAreaElement) {
          return createMessageHandler({ message, font: fontEl });
        }
        break;
      }
      case "letterhead": {
        const letterhead = panel.querySelector('[name="letterhead"]');
        if (letterhead instanceof HTMLInputElement) {
          return createLetterheadHandler({ letterhead, font: fontEl });
        }
        break;
      }
      case "address": {
        const line1 = panel.querySelector('[name="line1"]');
        const line2 = panel.querySelector('[name="line2"]');
        const line3 = panel.querySelector('[name="line3"]');
        if (
          line1 instanceof HTMLInputElement &&
          line2 instanceof HTMLInputElement &&
          line3 instanceof HTMLInputElement
        ) {
          return createAddressHandler({ line1, line2, line3, font: fontEl });
        }
        break;
      }
    }
    return null;
  }

  #wireTabNavigation(
    tabButtons: HTMLButtonElement[],
    getCurrentIndex: () => number,
    onSwitch: (index: number) => void,
  ): void {
    for (const btn of tabButtons) {
      btn.addEventListener("click", () => onSwitch(Number(btn.dataset.tabIndex)), {
        signal: this.signal,
      });
    }

    if (tabButtons.length <= 1) return;

    for (const btn of tabButtons) {
      btn.addEventListener(
        "keydown",
        (event) => {
          const count = tabButtons.length;
          const current = getCurrentIndex();
          let next = -1;
          if (event.key === "ArrowRight") next = (current + 1) % count;
          else if (event.key === "ArrowLeft") next = (current - 1 + count) % count;
          else if (event.key === "Home") next = 0;
          else if (event.key === "End") next = count - 1;
          if (next !== -1) {
            event.preventDefault();
            if (next !== current) {
              onSwitch(next);
              tabButtons[next].focus();
            }
          }
        },
        { signal: this.signal },
      );
    }
  }

  #wireInputListeners(root: HTMLElement): void {
    for (const field of root.querySelectorAll('input:not([type="checkbox"]), textarea')) {
      field.addEventListener(
        "input",
        () => {
          this.#clearPanelError(this.#getActivePanel());
          this.#updatePreviewStatus();
        },
        { signal: this.signal },
      );
    }

    for (const field of root.querySelectorAll('[name="dots"]')) {
      field.addEventListener("input", () => this.#updatePreviewStatus(), { signal: this.signal });
    }
  }

  #updatePreviewStatus(): void {
    const handler = this.#handlers[this.#activeTabIndex];
    if (handler) {
      this.#setPreviewStatus(handler, this.dataset.productTitle ?? "");
    }
  }

  #setPreviewStatus(handler: InputHandler, productTitle: string): void {
    const statusEl = this.refs.previewStatus;
    if (!statusEl) return;
    const text = handler.getText();
    if (text) {
      statusEl.textContent = `${productTitle} preview: ${text} in ${handler.getFont()}`;
    } else {
      statusEl.textContent = "";
    }
  }

  #showStageError(): void {
    const stage = this.refs.stage;
    if (stage) stage.hidden = true;
  }

  #getActivePanel(): HTMLElement | null {
    const panels = this.#asElementArray(this.refs.tabPanel);
    const panel = panels[this.#activeTabIndex];
    return panel instanceof HTMLElement ? panel : null;
  }

  #setActiveTabIndex(index: number): void {
    this.#activeTabIndex = index;

    const tabPanels = this.#asElementArray(this.refs.tabPanel);
    const tabButtons = this.#asButtonArray(this.refs.tabButton);

    for (let i = 0; i < tabPanels.length; i++) {
      const panel = tabPanels[i];
      if (panel instanceof HTMLElement) panel.hidden = i !== index;
    }
    for (let i = 0; i < tabButtons.length; i++) {
      const isActive = i === index;
      tabButtons[i].setAttribute("aria-selected", String(isActive));
      tabButtons[i].tabIndex = isActive ? 0 : -1;
    }
  }

  #switchTab(index: number): void {
    if (index === this.#activeTabIndex) return;
    this.#setActiveTabIndex(index);
    this.#clearPanelError(this.#getActivePanel());
    this.#updatePreviewStatus();

    const handler = this.#handlers[index];
    if (handler && this.#preview) {
      this.#preview.setInputHandler(handler);
    }
  }

  #clearPanelError(panel: HTMLElement | null | undefined): void {
    if (!panel) return;
    const errorEl = panel.querySelector("[data-form-error]");
    if (errorEl instanceof HTMLElement) errorEl.hidden = true;
    for (const field of panel.querySelectorAll("[aria-invalid]")) {
      field.removeAttribute("aria-invalid");
    }
  }

  #showPanelError(panel: HTMLElement, message: string): void {
    const errorEl = panel.querySelector("[data-form-error]");
    if (errorEl instanceof HTMLElement) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
  }

  #showValidationError(panel: HTMLElement | null | undefined, handler: InputHandler): void {
    if (!panel) return;

    const inputs = [
      ...panel.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
        'input:not([type="checkbox"]), textarea',
      ),
    ];
    const firstInvalid = inputs.find((el) => el.value.trim() === "") ?? inputs[0];

    if (firstInvalid instanceof HTMLElement) {
      firstInvalid.setAttribute("aria-invalid", "true");
      firstInvalid.focus();
    }

    const message =
      handler.getText() === ""
        ? (this.dataset.errorRequired ?? "")
        : (this.dataset.errorIncomplete ?? "");
    this.#showPanelError(panel, message);
  }

  #triggerBoundedFieldCounters(panel: HTMLElement | null | undefined): void {
    for (const field of panel?.querySelectorAll("bounded-field :is(input, textarea)") ?? []) {
      field.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }

  #handleConfirm = (): void => {
    if (this.#isBundleMode) {
      this.#handleBundleConfirm();
    } else {
      this.#handleSingleConfirm();
    }
  };

  #handleSingleConfirm(): void {
    const handler = this.#handlers[this.#activeTabIndex];
    if (!handler) return;

    if (!handler.validate()) {
      this.#showValidationError(this.#getActivePanel(), handler);
      return;
    }

    const lineId = this.#editContext?.properties._line_id ?? crypto.randomUUID();
    const preview = this.#preview?.captureImage() ?? null;

    const fontValue = handler.getFont();
    const properties: Record<string, string> = {
      Personalization: handler.getText(),
      Font: this.#fontEl?.getCartLabel(fontValue) ?? fontValue,
      _personalization: JSON.stringify({
        tabIndex: this.#activeTabIndex,
        ...handler.getRestoreData(),
      }),
      _line_id: lineId,
    };

    void this.#submitWithProperties(properties, preview);
  }

  async #submitWithProperties(
    properties: Record<string, string>,
    preview: string | null = null,
  ): Promise<void> {
    const button = this.refs.addToCart;
    if (!button) return;

    button.dataset.originalText = getButtonLabel(button);
    button.disabled = true;
    if (button.dataset.loadingText) {
      setButtonLabel(button, button.dataset.loadingText);
    }

    try {
      const action = this.#editContext ? "edit" : "create";
      if (this.#editContext) {
        await this.#updateCartItem(properties, preview);
      } else {
        await this.#addToCart(properties, preview);
        this.#resetFormState();
      }

      this.#publishPersonalizeAnalytics(action);

      const modal = this.closest("modal-panel");
      if (isModalPanelLike(modal)) modal.close();

      requestAnimationFrame(() => {
        const cartDrawer = document.querySelector("cart-drawer");
        if (isCartDrawerLike(cartDrawer)) {
          cartDrawer.openSuppressingAnalytics();
        }
      });
    } catch {
      if (button.dataset.errorText) {
        setButtonLabel(button, button.dataset.errorText);
        announce(button.dataset.errorText);
      }
    } finally {
      setTimeout(() => {
        button.disabled = false;
        setButtonLabel(button, button.dataset.originalText ?? "");
        delete button.dataset.originalText;
      }, 2000);
    }
  }

  #publishPersonalizeAnalytics(action: "create" | "edit"): void {
    const productId = this.dataset.productId;
    if (!productId) return;
    window.Shopify?.analytics?.publish?.("personalize_item", {
      action,
      items: [
        {
          item_id: `gid://shopify/Product/${productId}`,
          item_name: this.dataset.productTitle ?? "",
        },
      ],
    });
  }

  #deriveFeeProperties(bundleProperties: Record<string, string>): Record<string, string> {
    const feeProperties: Record<string, string> = {};
    for (const [key, value] of Object.entries(bundleProperties)) {
      if (key.startsWith("Personalization (") || key.startsWith("Font (")) {
        feeProperties[`_${key}`] = value;
      }
    }
    if (this.#isBundleMode) {
      const bundleTitle = this.dataset.productTitle;
      if (bundleTitle) {
        feeProperties._Bundle = bundleTitle;
      }
    }
    return feeProperties;
  }

  async #addToCart(properties: Record<string, string>, preview: string | null): Promise<void> {
    const variantId = Number(this.dataset.variantId);
    if (!variantId) return;

    const items: { id: number; quantity: number; properties: Record<string, string> }[] = [
      { id: variantId, quantity: 1, properties },
    ];

    const feeVariantId = Number(this.dataset.personalizationFeeVariantId);
    if (feeVariantId) {
      items.push({
        id: feeVariantId,
        quantity: 1,
        properties: {
          _personalization_for: properties._line_id,
          _hidden: "true",
          ...this.#deriveFeeProperties(properties),
        },
      });
    }

    await cartAdd(items);

    this.#persistPreview(properties._line_id, preview);

    const sectionHtml = await fetchCartSection("cart-drawer");
    document.dispatchEvent(
      new CustomEvent("cart:render", {
        detail: { html: sectionHtml, announcement: this.dataset.announcementAdded },
      }),
    );
  }

  async #updateCartItem(
    newProperties: Record<string, string>,
    preview: string | null,
  ): Promise<void> {
    if (!this.#editContext) return;

    const { key, quantity, properties: existingProperties } = this.#editContext;
    const mergedProperties = { ...existingProperties, ...newProperties };

    await cartChange({ id: key, quantity, properties: mergedProperties });

    const feeLineKey = this.#editContext.feeLineKey;
    if (this.#isBundleMode && feeLineKey) {
      await cartChange({
        id: feeLineKey,
        quantity: 1,
        properties: {
          _personalization_for: mergedProperties._line_id,
          _hidden: "true",
          ...this.#deriveFeeProperties(mergedProperties),
        },
      });
    }

    this.#persistPreview(mergedProperties._line_id, preview);

    const sectionHtml = await fetchCartSection("cart-drawer");
    document.dispatchEvent(
      new CustomEvent("cart:render", {
        detail: { html: sectionHtml, announcement: this.dataset.announcementUpdated },
      }),
    );
  }

  #persistPreview(lineId: string | undefined, preview: string | null): void {
    if (!preview || !lineId) return;
    try {
      localStorage.setItem(`${PREVIEW_KEY_PREFIX}${lineId}`, preview);
    } catch {
      // ignore quota errors
    }
  }
}

if (!customElements.get("product-personalization")) {
  customElements.define("product-personalization", ProductPersonalization);
}
